import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { bookingAPI } from '../../api/bookings';
import { businessAPI } from '../../api/businesses';
import { subscriptionAPI } from '../../api/subscriptions';
import StatsCards from './StatsCards';
import RecentBookings from './RecentBookings';
import BookingTrends from '../reports/BookingTrends';
import LoadingSpinner from '../common/LoadingSpinner';
import BusinessSearch from './BusinessSearch';
import FeaturedBusinesses from './FeaturedBusinesses';
import CategoryFilter from './CategoryFilter';
import BusinessCard from './BusinessCard';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-calendar/dist/Calendar.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isBusinessOwner, isAdmin, isCustomer } = useAuth();
  const [stats, setStats] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Customer-specific state
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [showAllBusinessesModal, setShowAllBusinessesModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
    if (isCustomer) {
      loadBusinesses();
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isBusinessOwner) {
        // Business owner dashboard
        try {
          const businessRes = await businessAPI.getMyBusiness();
          const businessData = businessRes.data.data;
          setBusiness(businessData);
          
          const bookingsRes = await bookingAPI.getMyBookings();
          const bookingsData = bookingsRes.data.data || [];
          
          // Accurate Business Metrics
          const pending = bookingsData.filter(b => b.status === 'pending').length;
          const active = bookingsData.filter(b => b.status === 'confirmed').length;
          const completed = bookingsData.filter(b => b.status === 'completed').length;
          const cancelled = bookingsData.filter(b => b.status === 'cancelled').length;
          const revenue = bookingsData
            .filter(b => b.status === 'completed')
            .reduce((sum, b) => sum + parseFloat(b.total_price || 0), 0);
          const uniqueCustomers = new Set(bookingsData.map(b => b.customer_id)).size;
          
          setStats({
            total_bookings: bookingsData.length,
            pending_bookings: pending,
            active_bookings: active,
            completed_bookings: completed,
            cancelled_bookings: cancelled,
            total_revenue: revenue,
            unique_customers: uniqueCustomers
          });
          
          setAllBookings(bookingsData);
        } catch (busErr) {
          console.log('Error loading business owner data:', busErr.message);
        }
      } else if (isCustomer) {
        // Customer dashboard
        const bookingsRes = await bookingAPI.getMyBookings();
        const bookingsData = bookingsRes.data.data || [];
        
        // Accurate Customer Metrics
        const upcoming = bookingsData.filter(b => b.status === 'confirmed' || b.status === 'pending').length;
        const completed = bookingsData.filter(b => b.status === 'completed').length;
        const cancelled = bookingsData.filter(b => b.status === 'cancelled').length;
        
        setAllBookings(bookingsData);
        setStats({
          total_bookings: bookingsData.length,
          upcoming_bookings: upcoming,
          completed_bookings: completed,
          cancelled_bookings: cancelled
        });
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings based on selected date
  useEffect(() => {
    if (allBookings.length > 0) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      const filtered = allBookings.filter(booking => {
        if (!booking.booking_date) return false;
        let bDateStr = "";
        if (typeof booking.booking_date === 'string') {
          bDateStr = booking.booking_date.split('T')[0];
        } else {
          const d = new Date(booking.booking_date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          bDateStr = `${year}-${month}-${day}`;
        }
        return bDateStr === dateStr;
      });
      
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings([]);
    }
  }, [selectedDate, allBookings]);

  const loadBusinesses = async () => {
    try {
      const response = await businessAPI.getAll();
      const payload = response?.data?.data;
      const businessesData = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.businesses) ? payload.businesses : []);
      setAllBusinesses(businessesData);
      setFeaturedBusinesses(businessesData.slice(0, 4));
      setFilteredBusinesses(businessesData);
    } catch (error) {
      console.error('Failed to load businesses:', error);
      // Fallback to empty if API fails
      setAllBusinesses([]);
    }
  };

  // Filter businesses based on category and search
  useEffect(() => {
    let filtered = allBusinesses;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(b => b.business_type === selectedCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredBusinesses(filtered);
  }, [selectedCategory, searchTerm, allBusinesses]);

  if (loading) return <LoadingSpinner />;

  // Customer View
  if (isCustomer) {
    return (
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.first_name}! 👋</h1>
          <p className="text-primary-100">Discover and book the best services near you</p>
        </div>

        {/* Stats Cards for Customer */}
        <StatsCards stats={stats} userRole={user?.role} business={business} />

        {/* Search Bar */}
        <BusinessSearch 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
        />

        {/* Category Filter */}
        <CategoryFilter 
          selectedCategory={selectedCategory} 
          setSelectedCategory={setSelectedCategory} 
        />

        {/* Featured Businesses Section */}
        <FeaturedBusinesses businesses={featuredBusinesses} />

        {/* All Businesses Grid */}
        <div id="all-businesses">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {searchTerm || selectedCategory !== 'all' ? 'Search Results' : 'All Businesses'}
            </h2>
            <div className="flex items-center gap-3">
              <p className="text-gray-600">{filteredBusinesses.length} businesses found</p>
              <button
                type="button"
                onClick={() => setShowAllBusinessesModal(true)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View All
              </button>
            </div>
          </div>
          
          {filteredBusinesses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No businesses found. Try adjusting your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBusinesses.slice(0, 6).map((business) => (
                <BusinessCard 
                  key={business.id} 
                  business={business} 
                />
              ))}
            </div>
          )}

          {filteredBusinesses.length > 6 && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => setShowAllBusinessesModal(true)}
                className="btn-primary"
              >
                View All Businesses
              </button>
            </div>
          )}
        </div>

        {showAllBusinessesModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAllBusinessesModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">All Businesses</h2>
                  <p className="text-sm text-gray-600">{filteredBusinesses.length} businesses found</p>
                </div>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-800"
                  onClick={() => setShowAllBusinessesModal(false)}
                >
                  Close
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <input
                      type="text"
                      placeholder="Search businesses..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full input"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full input"
                    >
                      <option value="all">All Categories</option>
                      <option value="salon">Salon / Spa</option>
                      <option value="clinic">Clinic / Medical</option>
                      <option value="gym">Gym / Fitness</option>
                      <option value="ride">Ride / Transportation</option>
                    </select>
                  </div>
                </div>

                {filteredBusinesses.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <p className="text-gray-500">No businesses found. Try adjusting your search.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBusinesses.map((business) => (
                      <BusinessCard key={business.id} business={business} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Bookings Section with Calendar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Calendar Column */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
              <div className="p-6 bg-primary-600 text-white">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Select Date
                </h2>
              </div>
              <div className="p-4 dashboard-calendar">
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  className="w-full border-none"
                  tileClassName={({ date }) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const hasBookings = allBookings.some(b => {
                      if (!b.booking_date) return false;
                      let bDateStr = "";
                      if (typeof b.booking_date === 'string') {
                        bDateStr = b.booking_date.split('T')[0];
                      } else {
                        const d = new Date(b.booking_date);
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        bDateStr = `${year}-${month}-${day}`;
                      }
                      return bDateStr === dateStr;
                    });
                    return hasBookings ? 'has-booking-dot' : '';
                  }}
                />
              </div>
            </div>
            <div className="bg-primary-50 rounded-2xl p-4 border border-primary-100">
              <p className="text-sm text-primary-700">Select a date to see your appointments for that day.</p>
            </div>
          </div>

          {/* Bookings List Column */}
          <div className="lg:col-span-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Bookings for {format(selectedDate, 'MMMM dd, yyyy')}
              </h2>
              <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm font-bold">
                {filteredBookings.length} {filteredBookings.length === 1 ? 'Booking' : 'Bookings'}
              </span>
             </div>
             <RecentBookings bookings={filteredBookings} hideHeader={true} />
           </div>
         </div>
      </div>
    );
  }

  // Business Owner View
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.first_name}!</p>
      </div>

      <StatsCards stats={stats} userRole={user?.role} business={business} />
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Calendar & Stats Column */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
            <div className="p-6 bg-indigo-600 text-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Select Date
              </h2>
            </div>
            <div className="p-4 dashboard-calendar">
              <Calendar
                onChange={setSelectedDate}
                value={selectedDate}
                className="w-full border-none"
                tileClassName={({ date }) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const hasBookings = allBookings.some(b => {
                    if (!b.booking_date) return false;
                    let bDateStr = "";
                    if (typeof b.booking_date === 'string') {
                      bDateStr = b.booking_date.split('T')[0];
                    } else {
                      const d = new Date(b.booking_date);
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      bDateStr = `${year}-${month}-${day}`;
                    }
                    return bDateStr === dateStr;
                  });
                  return hasBookings ? 'has-booking-dot' : '';
                }}
              />
            </div>
          </div>
          <BookingTrends businessId={business?.id} />
        </div>

        {/* Bookings List Column */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-2 h-8 bg-indigo-600 rounded-full"></div>
              Schedule for {format(selectedDate, 'MMMM dd, yyyy')}
            </h2>
            <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-bold shadow-sm">
              {filteredBookings.length} {filteredBookings.length === 1 ? 'Appointment' : 'Appointments'}
            </span>
          </div>
          <RecentBookings bookings={filteredBookings} hideHeader={true} />
        </div>
      </div>

      <style jsx global>{`
        .dashboard-calendar .react-calendar {
          border: none !important;
          font-family: inherit;
          width: 100% !important;
        }
        .dashboard-calendar .react-calendar__tile--now {
          background: #eef2ff !important;
          color: #4f46e5 !important;
          font-weight: bold;
          border-radius: 12px;
        }
        .dashboard-calendar .react-calendar__tile--active {
          background: #4f46e5 !important;
          color: white !important;
          border-radius: 12px;
        }
        .dashboard-calendar .react-calendar__tile {
          padding: 12px 8px !important;
          position: relative;
        }
        .dashboard-calendar .has-booking-dot::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background-color: #4f46e5;
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
